/**
 * Code-split registry of the public product demos.
 *
 * Each demo mounts a real slice of the authenticated app — the candidate panel
 * alone pulls in nine destination tabs — so importing the whole set statically
 * would put the product's component graph in the bundle of every marketing page,
 * including the ones that show a single screen. Every screen is loaded on demand
 * instead, and a page only pays for the screens it actually renders.
 *
 * Deferring the *mount* (see the feature page's viewport-gated wrapper) is not
 * enough on its own: the code still has to arrive lazily for that to be a saving.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { DemoCrop } from './DemoScreen';

/** Every demo takes the same one prop: how much of the screen to show. */
export type DemoScreenComponent = ComponentType<{ crop?: DemoCrop }>;

export const DEMO_SCREENS = {
  ApplicationsBoardScreen: lazy(() =>
    import('./screens/ApplicationsBoardScreen').then((m) => ({ default: m.ApplicationsBoardScreen })),
  ),
  CandidateDeepDiveScreen: lazy(() =>
    import('./screens/CandidateDeepDiveScreen').then((m) => ({ default: m.CandidateDeepDiveScreen })),
  ),
  CandidateNextStepsScreen: lazy(() =>
    import('./screens/CandidateNextStepsScreen').then((m) => ({ default: m.CandidateNextStepsScreen })),
  ),
  CandidatePreferencesScreen: lazy(() =>
    import('./screens/CandidatePreferencesScreen').then((m) => ({
      default: m.CandidatePreferencesScreen,
    })),
  ),
  CandidateProfileScreen: lazy(() =>
    import('./screens/CandidateProfileScreen').then((m) => ({ default: m.CandidateProfileScreen })),
  ),
  InterviewRecapScreen: lazy(() =>
    import('./screens/InterviewRecapScreen').then((m) => ({ default: m.InterviewRecapScreen })),
  ),
  InterviewStrategyScreen: lazy(() =>
    import('./screens/InterviewStrategyScreen').then((m) => ({ default: m.InterviewStrategyScreen })),
  ),
  JobDistributionScreen: lazy(() =>
    import('./screens/JobDistributionScreen').then((m) => ({ default: m.JobDistributionScreen })),
  ),
  JobDraftScreen: lazy(() =>
    import('./screens/JobDraftScreen').then((m) => ({ default: m.JobDraftScreen })),
  ),
  MatchScorecardScreen: lazy(() =>
    import('./screens/MatchScorecardScreen').then((m) => ({ default: m.MatchScorecardScreen })),
  ),
  MockInterviewFeedbackScreen: lazy(() =>
    import('./screens/MockInterviewFeedbackScreen').then((m) => ({
      default: m.MockInterviewFeedbackScreen,
    })),
  ),
  MockInterviewSessionScreen: lazy(() =>
    import('./screens/MockInterviewSessionScreen').then((m) => ({
      default: m.MockInterviewSessionScreen,
    })),
  ),
  SchedulingScreen: lazy(() =>
    import('./screens/SchedulingScreen').then((m) => ({ default: m.SchedulingScreen })),
  ),
} satisfies Record<string, LazyExoticComponent<DemoScreenComponent>>;

export type DemoScreenName = keyof typeof DEMO_SCREENS;

/**
 * Screens built on the candidate panel, whose section rail is a live control.
 * They can be explored section by section; the rest can only be scrolled.
 */
const SCREENS_WITH_SECTIONS: ReadonlySet<DemoScreenName> = new Set<DemoScreenName>([
  'CandidateDeepDiveScreen',
  'CandidateNextStepsScreen',
  'CandidatePreferencesScreen',
  'CandidateProfileScreen',
  'InterviewRecapScreen',
  'InterviewStrategyScreen',
  'MatchScorecardScreen',
]);

/**
 * The one line that tells a visitor the frame holds the running product rather
 * than a screenshot, and what they can do with it.
 */
export function demoScreenHint(name: DemoScreenName): string {
  /*
    Device-neutral wording. The rail sits beside the panel on a desktop and
    above it on a phone, "click" is wrong on a touch screen, and only a desktop
    screen scrolls inside its frame — a phone gets an expand control instead.
    How to see more is therefore left to the viewport itself, which knows which
    form it is in; this line only says what the frame holds.
  */
  return SCREENS_WITH_SECTIONS.has(name)
    ? 'This is the real product running on sample data. Open any section to explore it.'
    : 'This is the real product running on sample data, not a screenshot.';
}
